import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiEdit2 } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import Drawer from '../../components/Drawer/Drawer';
import drawerStyles from '../../components/Drawer/Drawer.module.css';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminPartners.module.css';
import axiosClient from '../../api/axiosClient';

interface Application {
  id: string; tier: string; full_name: string; email: string; phone: string | null;
  fund_org: string | null; meeting_type: string | null; preferred_date: string | null; city: string | null;
  message: string | null; source: string; status: string; created_at: string;
}
interface Tier { key: string; name: string; investCapital: number; seats: number; seatsFilled: number; seatsAvailable: number; }

const STATUS_COLORS: Record<string, string> = { pending: '#e8a86a', approved: '#5cb87d', rejected: '#e2534a' };

const AdminPartners: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [raiseTargetUsd, setRaiseTargetUsd] = useState<number>(0);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState<Application | null>(null);
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [seatsInput, setSeatsInput] = useState('');
  const [priceInput, setPriceInput] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      axiosClient.get('/admin/partners', { params: filter !== 'all' ? { status: filter } : {} }),
      axiosClient.get('/partners/tiers'),
    ]).then(([a, t]) => {
      setApplications(a.data.applications);
      setTiers(t.data.tiers);
      setRaiseTargetUsd(Number(t.data.raiseTargetUsd) || 0);
    }).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id: string, status: string) => {
    try {
      await axiosClient.patch(`/admin/partners/${id}/status`, { status });
      toast.success(`Marked as ${status}.`);
      setSelected(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update status.');
    }
  };

  const openEditTier = (t: Tier) => {
    setEditingTier(t.key);
    setSeatsInput(String(t.seats));
    setPriceInput(String(t.investCapital));
  };

  const saveTier = async (tierKey: string) => {
    try {
      await axiosClient.patch(`/admin/partners/tiers/${tierKey}`, {
        seats: Number(seatsInput),
        investCapital: Number(priceInput),
      });
      toast.success('Tier updated.');
      setEditingTier(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update tier.');
    }
  };

  const openEditTarget = () => {
    setTargetInput(String(raiseTargetUsd));
    setEditingTarget(true);
  };
  const saveTarget = async () => {
    try {
      await axiosClient.patch('/admin/partners/raise-target', { raiseTargetUsd: Number(targetInput) });
      toast.success('Raise target updated.');
      setEditingTarget(false);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update raise target.');
    }
  };

  return (
    <AdminShell>
      <PageHeader title="Partner Applications" subtitle="Institutional Partner Program — applications from the landing page and dashboard." action={<AdminWalletBadge />} />

      <div className={panelStyles.page}>
        <div className={styles.seatCard} style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div className={styles.seatCardHeader} style={{ marginBottom: 4 }}><span>Total raise target</span></div>
            {editingTarget ? (
              <div className={styles.seatEditRow}>
                <span style={{ color: 'var(--muted)' }}>$</span>
                <input
                  type="number" min={1} value={targetInput}
                  onChange={(e) => setTargetInput(e.target.value)}
                  className={styles.seatInput}
                  style={{ width: 140 }}
                />
                <button className={styles.seatSaveBtn} onClick={saveTarget}>Save</button>
                <button className={styles.seatCancelBtn} onClick={() => setEditingTarget(false)}>×</button>
              </div>
            ) : (
              <b style={{ fontSize: 22 }}>${raiseTargetUsd.toLocaleString()}</b>
            )}
          </div>
          {!editingTarget && <button className={styles.editSeatBtn} onClick={openEditTarget}><FiEdit2 /></button>}
        </div>

        <div className={styles.seatsGrid}>
          {tiers.map((t) => (
            <div key={t.key} className={styles.seatCard}>
              <div className={styles.seatCardHeader}>
                <span>{t.name}</span>
                <button className={styles.editSeatBtn} onClick={() => openEditTier(t)}><FiEdit2 /></button>
              </div>
              {editingTier === t.key ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className={styles.seatEditRow}>
                    <span style={{ fontSize: 11, color: 'var(--muted)', width: 40 }}>Seats</span>
                    <input
                      type="number" min={1} value={seatsInput}
                      onChange={(e) => setSeatsInput(e.target.value)}
                      className={styles.seatInput}
                    />
                  </div>
                  <div className={styles.seatEditRow}>
                    <span style={{ fontSize: 11, color: 'var(--muted)', width: 40 }}>Price $</span>
                    <input
                      type="number" min={1} value={priceInput}
                      onChange={(e) => setPriceInput(e.target.value)}
                      className={styles.seatInput}
                    />
                  </div>
                  <div className={styles.seatEditRow}>
                    <button className={styles.seatSaveBtn} onClick={() => saveTier(t.key)}>Save</button>
                    <button className={styles.seatCancelBtn} onClick={() => setEditingTier(null)}>×</button>
                  </div>
                </div>
              ) : (
                <>
                  <b>${t.investCapital.toLocaleString()}</b>
                  <div className={styles.seatSub}>{t.seatsFilled} / {t.seats} seats filled — {t.seatsAvailable} remaining</div>
                  <div className={styles.seatBar}><div className={styles.seatBarFill} style={{ width: `${(t.seatsFilled / t.seats) * 100}%` }} /></div>
                </>
              )}
            </div>
          ))}
        </div>

        <div className={panelStyles.filters}>
          {['all', 'pending', 'approved', 'rejected'].map((f) => (
            <button key={f} className={`${panelStyles.filterBtn} ${filter === f ? panelStyles.filterBtnActive : ''}`} onClick={() => setFilter(f)}>
              {f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div className={panelStyles.tableWrap}>
          {loading && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}
          {!loading && applications.length === 0 && <div className={panelStyles.empty}>No applications match this filter.</div>}
          {!loading && applications.length > 0 && (
            <table className={panelStyles.table}>
              <thead><tr><th>Applicant</th><th>Tier</th><th>Source</th><th>Status</th></tr></thead>
              <tbody>
                {applications.map((a) => (
                  <tr key={a.id} onClick={() => setSelected(a)} style={{ cursor: 'pointer' }}>
                    <td><div className={panelStyles.nameCell}>{a.full_name}</div><div className={panelStyles.subCell}>{a.email}</div></td>
                    <td className={panelStyles.nameCell}>{a.tier}</td>
                    <td className={panelStyles.subCell}>{a.source === 'landing_meeting' ? 'Landing page' : 'Dashboard'}</td>
                    <td><span className={panelStyles.statusPill} style={{ color: STATUS_COLORS[a.status], borderColor: STATUS_COLORS[a.status] }}>{a.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Drawer open={!!selected} onClose={() => setSelected(null)} title="Partner application">
        {selected && (
          <>
            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}>Applicant</div>
              <div className={drawerStyles.detailValue}>{selected.full_name}</div>
              <div className={panelStyles.subCell}>{selected.email} · {selected.phone || 'No phone'}</div>
            </div>
            <div className={drawerStyles.detailGrid}>
              <div className={drawerStyles.detailRow}><div className={drawerStyles.detailLabel}>Tier</div><div className={drawerStyles.detailValue}>{selected.tier}</div></div>
              <div className={drawerStyles.detailRow}><div className={drawerStyles.detailLabel}>Fund/Org</div><div className={drawerStyles.detailValue}>{selected.fund_org || '—'}</div></div>
              <div className={drawerStyles.detailRow}><div className={drawerStyles.detailLabel}>Meeting Type</div><div className={drawerStyles.detailValue}>{selected.meeting_type || '—'}</div></div>
              <div className={drawerStyles.detailRow}><div className={drawerStyles.detailLabel}>Preferred Date</div><div className={drawerStyles.detailValue}>{selected.preferred_date || '—'}</div></div>
              <div className={drawerStyles.detailRow}><div className={drawerStyles.detailLabel}>City</div><div className={drawerStyles.detailValue}>{selected.city || '—'}</div></div>
              <div className={drawerStyles.detailRow}><div className={drawerStyles.detailLabel}>Submitted</div><div className={drawerStyles.detailValue}>{new Date(selected.created_at).toLocaleString()}</div></div>
            </div>
            {selected.message && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Message</div>
                <div className={drawerStyles.detailValue}>{selected.message}</div>
              </div>
            )}
          </>
        )}
        {selected && (
          <div className={drawerStyles.footer} style={{ position: 'sticky', bottom: 0, background: 'var(--panel)', margin: '24px -24px -24px', flexWrap: 'wrap' }}>
            {selected.status !== 'approved' && (
              <button className={panelStyles.actionBtn} onClick={() => updateStatus(selected.id, 'approved')}>Approve</button>
            )}
            {selected.status !== 'rejected' && (
              <button className={panelStyles.actionBtnReject} onClick={() => updateStatus(selected.id, 'rejected')}>Reject</button>
            )}
          </div>
        )}
      </Drawer>
    </AdminShell>
  );
};

export default AdminPartners;
